'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { useIsMobile } from './use-is-mobile'

// Shared modal shell — Radix Dialog underneath (focus trap, aria wiring,
// body scroll lock, portal, Escape + backdrop dismiss for free), styled to
// the Roostr M3 Expressive language: dim scrim, rounded-shape-xl surface
// panel with the standard 24/26 padding, optional bottom-sheet presentation
// on mobile viewports. Bespoke modals (FilterModal's railed two-pane layout)
// intentionally stay hand-rolled.
export interface RModalProps {
  open: boolean
  onClose: () => void
  /** Panel width in px (default 520; capped at 100% by max-w-full). */
  width?: number
  /** Present as a bottom sheet on mobile viewports (default false = centered). */
  mobileSheet?: boolean
  /** Accessible name for the dialog — rendered as a screen-reader-only title. */
  ariaLabel?: string
  children: React.ReactNode
}

export function RModal({
  open,
  onClose,
  width = 520,
  mobileSheet = false,
  ariaLabel,
  children,
}: RModalProps) {
  const mobile = useIsMobile()
  const sheet = mobileSheet && mobile
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogPrimitive.Portal>
        {/* Content nests inside Overlay (Radix scrollable-overlay pattern) so
            the scrim owns centering/sheet alignment exactly like the previous
            hand-rolled shells did. */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[90] flex justify-center bg-[rgba(26,23,23,0.42)]',
            sheet ? 'items-end p-0' : 'items-center p-6'
          )}
        >
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className={cn(
              'max-w-full animate-card-in bg-surface p-[24px_26px] shadow-e3 outline-none',
              sheet ? 'rounded-t-[24px]' : 'rounded-shape-xl'
            )}
            style={{ width }}
          >
            {ariaLabel ? (
              <DialogPrimitive.Title className="sr-only">{ariaLabel}</DialogPrimitive.Title>
            ) : null}
            {children}
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
